/* eslint-disable @typescript-eslint/ban-types */
import './_debug_hook';
import { useLayoutEffect } from 'react';
import { PRESENTATION_DEBUG_HOOK } from './subscribable/common';
import type { SynchronousPresentation } from './subscribable/synchronous_presentation';
import type { ConcurrentPresentation } from './subscribable/concurrent_presentation';
import type { FlowPresentation, CurrPresentationTuple } from './subscribable/flow_presentation';
import { useMisbehavedForceUpdate } from './_use_misbehaved_force_update';

/**
 * ## cortex:presentation:usePresentation
 * `usePresentation` will trigger a component render if a property explicitly changed.
 * By providing deps you can silent triggers for the specified properties.
 *
 * @example
 * function Component() {
 *    const service = useService(MyController);
 *    const presentation = usePresentation(MyController.presentation);
 *
 *    return <div>{presentation.name}</div>
 * }
 *
 * @public
 */
export function usePresentation<T extends object>(state: SynchronousPresentation<T>, deps?: ReadonlyArray<keyof T>): Readonly<T>;
export function usePresentation<T extends object>(state: ConcurrentPresentation<T>, deps?: ReadonlyArray<keyof T>): Readonly<T>;
export function usePresentation<T extends object>(state: FlowPresentation<T>, deps?: ReadonlyArray<keyof T>): CurrPresentationTuple<T>;
export function usePresentation<T extends object>(
	presentation: SynchronousPresentation<T> | ConcurrentPresentation<T> | FlowPresentation<T>,
	triggers?: ReadonlyArray<keyof T>,
): CurrPresentationTuple<T> | Readonly<T> {
	const forceUpdate = useMisbehavedForceUpdate();

	useLayoutEffect(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		//@ts-ignore
		globalThis[PRESENTATION_DEBUG_HOOK].emit('add', presentation);

		const disposer = presentation.subscribe((changeSet) => {
			if (triggers && changeSet) {
				const shouldUpdate = triggers.find((trigger) => {
					return changeSet.has(trigger);
				});

				if (shouldUpdate) {
					forceUpdate();
				}

				return;
			}

			forceUpdate();
		});

		return () => {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			//@ts-ignore
			globalThis[PRESENTATION_DEBUG_HOOK].emit('remove', presentation);
			disposer();
		};
		// TODO: See if it's safe to add forceUpdate as a dependency
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [presentation, triggers]);

	return presentation.read();
}
